import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type { Building, Source } from "@types"
import type {
    AlarmConfig,
    ThresholdPoint,
} from "@modules/energy-manager/types/alarme"

/**
 * Service alarmes building/source.
 */
export class AlarmeService {
    /**
     * Récupère un profil variable pour un building.
     */
    async getBuildingThreshold(buildingId: string, variable: string) {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/building/${buildingId}/profil/${variable}`
            )
            return data?.profil?.[variable] ?? []
        })
    }

    /**
     * Récupère tous les profils d'un building.
     */
    async getBuildingThresholdByData(buildingId: string) {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/building/${buildingId}/profil`
            )
            return data?.profil ?? {}
        })
    }

    /**
     * Données pour auto-génération du profil (graph + métadonnées formulaire).
     * GET /v5/building/:buildingId/profil/:variable/data?temp=hourly|daily
     */
    async getBuildingProfileByData(
        buildingId: string,
        variable: string,
        freq: "hourly" | "daily",
        options?: { signal?: AbortSignal }
    ) {
        return handleServiceCall(async () => {
            const { data } = await axios.get<{
                profils?: unknown
                graph?: ThresholdPoint[]
            }>(
                `/api/metier/v5/building/${buildingId}/profil/${variable}/data`,
                { params: { temp: freq }, signal: options?.signal }
            )
            return {
                profilesFormInformation: data?.profils ?? null,
                points: (data?.graph ?? []) as ThresholdPoint[],
            }
        })
    }

    /**
     * Liste des bâtiments ayant un profil pour une variable (import).
     * GET /v5/building/profil/:variable
     */
    async getBuildingsByProfile(variable: string) {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/building/profil/${variable}`
            )
            return data
        })
    }

    /**
     * Ajoute un profil variable sur un building.
     */
    async addBuildingThreshold(
        buildingId: string,
        variable: string,
        points: ThresholdPoint[],
        options?: { signal?: AbortSignal }
    ) {
        return handleServiceCall(async () => {
            const { data } = await axios.post<Building>(
                `/api/metier/v5/building/${buildingId}/profil/${variable}`,
                points,
                { signal: options?.signal }
            )
            return data
        })
    }

    /**
     * Met à jour un profil variable sur un building.
     */
    async updateBuildingThreshold(
        buildingId: string,
        variable: string,
        points: ThresholdPoint[]
    ) {
        return handleServiceCall(async () => {
            const { data } = await axios.put<Building>(
                `/api/metier/v5/building/${buildingId}/profil/${variable}`,
                points
            )
            return data
        })
    }

    /**
     * Supprime un profil variable sur un building.
     */
    async removeBuildingThreshold(buildingId: string, variable: string) {
        return handleServiceCall(async () => {
            await axios.delete(
                `/api/metier/v5/building/${buildingId}/profil/${variable}`
            )
        })
    }

    /**
     * Met à jour une alarme variable sur un building.
     */
    async updateBuildingAlarm(
        buildingId: string,
        variable: string,
        alarm: AlarmConfig,
        options?: { signal?: AbortSignal }
    ) {
        return handleServiceCall(async () => {
            await axios.put(
                `/api/metier/v5/building/${buildingId}/alarme/${variable}`,
                alarm,
                { signal: options?.signal }
            )
        })
    }

    /**
     * Abonne l'utilisateur à une alarme building.
     */
    async subscribeBuildingAlarm(
        buildingId: string,
        variable: string,
        key: string
    ) {
        return handleServiceCall(async () => {
            await axios.post(
                `/api/metier/v5/building/${buildingId}/alarme/${variable}/subscribe`,
                { key }
            )
        })
    }

    /**
     * Désabonne l'utilisateur d'une alarme building.
     */
    async unsubscribeBuildingAlarm(
        buildingId: string,
        variable: string,
        key: string
    ) {
        return handleServiceCall(async () => {
            await axios.post(
                `/api/metier/v5/building/${buildingId}/alarme/${variable}/unsubscribe`,
                { key }
            )
        })
    }

    /**
     * Récupère un profil variable pour une source.
     */
    async getSourceThreshold(sourceId: string, variable: string) {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/source/${sourceId}/profil/${variable}`
            )
            return data?.profil?.[variable] ?? []
        })
    }

    /**
     * Récupère tous les profils d'une source.
     */
    async getSourceThresholdByData(sourceId: string) {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/source/${sourceId}/profil`
            )
            return data?.profil ?? {}
        })
    }

    /**
     * Données pour auto-génération du profil (graph + métadonnées formulaire).
     * GET /v5/source/:sourceId/profil/:variable/data?temp=hourly|daily
     */
    async getSourceProfileByData(
        sourceId: string,
        variable: string,
        freq: "hourly" | "daily",
        options?: { signal?: AbortSignal }
    ) {
        return handleServiceCall(async () => {
            const { data } = await axios.get<{
                profils?: unknown
                graph?: ThresholdPoint[]
            }>(`/api/metier/v5/source/${sourceId}/profil/${variable}/data`, {
                params: { temp: freq },
                signal: options?.signal,
            })
            return {
                profilesFormInformation: data?.profils ?? null,
                points: (data?.graph ?? []) as ThresholdPoint[],
            }
        })
    }

    /**
     * Liste des sources ayant un profil pour une variable (import).
     * GET /v5/source/profil/:variable
     */
    async getSourcesByProfile(variable: string) {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/source/profil/${variable}`
            )
            return data
        })
    }

    /**
     * Ajoute un profil variable sur une source.
     */
    async addSourceThreshold(
        sourceId: string,
        variable: string,
        points: ThresholdPoint[]
    ) {
        return handleServiceCall(async () => {
            const { data } = await axios.post<Source>(
                `/api/metier/v5/source/${sourceId}/profil/${variable}`,
                points
            )
            return data
        })
    }

    /**
     * Met à jour un profil variable sur une source.
     */
    async updateSourceThreshold(
        sourceId: string,
        variable: string,
        points: ThresholdPoint[]
    ) {
        return handleServiceCall(async () => {
            const { data } = await axios.put<Source>(
                `/api/metier/v5/source/${sourceId}/profil/${variable}`,
                points
            )
            return data
        })
    }

    /**
     * Supprime un profil variable sur une source.
     */
    async removeSourceThreshold(sourceId: string, variable: string) {
        return handleServiceCall(async () => {
            await axios.delete(
                `/api/metier/v5/source/${sourceId}/profil/${variable}`
            )
        })
    }

    /**
     * Met à jour une alarme variable sur une source.
     */
    async updateSourceAlarm(
        sourceId: string,
        variable: string,
        alarm: AlarmConfig
    ) {
        return handleServiceCall(async () => {
            await axios.put(
                `/api/metier/v5/source/${sourceId}/alarme/${variable}`,
                alarm
            )
        })
    }

    /**
     * Abonne l'utilisateur à une alarme source.
     */
    async subscribeSourceAlarm(
        sourceId: string,
        variable: string,
        key: string
    ) {
        return handleServiceCall(async () => {
            await axios.post(
                `/api/metier/v5/source/${sourceId}/alarme/${variable}/subscribe`,
                { key }
            )
        })
    }

    /**
     * Désabonne l'utilisateur d'une alarme source.
     */
    async unsubscribeSourceAlarm(
        sourceId: string,
        variable: string,
        key: string
    ) {
        return handleServiceCall(async () => {
            await axios.post(
                `/api/metier/v5/source/${sourceId}/alarme/${variable}/unsubscribe`,
                { key }
            )
        })
    }
}
